import Icon from '@mdi/react';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuIndicator,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  NavigationMenuViewport,
  pdsTv,
} from '@nestle-npus-nbm/pds-ui';
import { graphql, useStaticQuery } from 'gatsby';
import { useFlags } from 'gatsby-plugin-launchdarkly';
import { sendIt } from 'gatsby-plugin-purina-analytics/common/functions';
import { isEmpty } from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { mdiEarth } from 'src/assets/icons/mdiIcons';
import { LDFlags } from 'src/common/launchDarkly';
import { isServer } from 'src/utils/environment';
import { GeoMenu } from 'src/utils/geoMenu';
import { getAnchorTarget } from 'src/utils/url';
import { BaseLink, LinkProps as GatsbyLinkProps } from '../Link';
import MediaImage from '../media/MediaImage';

const GEO_MENU_ID = 'geo-menu' as const;

const separator = pdsTv({
  base: 'pds-flex pds-items-center pds-justify-center after:pds-h-[0.875rem] after:pds-w-[1px] after:pds-bg-surface after:pds-content-[""] last:after:pds-content-none',
});

const forceMount = isServer() ? ({ forceMount: true } as const) : {};

const ParagraphMenuListItem: React.FC<GatsbyLinkProps> = ({ children, ...props }) => {
  const handleAnalytics = () => {
    sendIt({
      event: 'nav_click',
      eventParams: {
        nav_item: props.title,
        nav_type: 'header',
      },
    });
  };

  return (
    <li className="pds-list-none pds-bg-paper pds-px-4.5 pds-py-4 has-[*:hover]:pds-bg-gray-100">
      <BaseLink
        analytics={false}
        onClick={handleAnalytics}
        className="pds-flex pds-max-w-max pds-gap-3 pds-rounded-sm pds-text-body-md pds-leading-none pds-text-paper-text pds-no-underline hover:pds-text-paper-text focus:pds-outline focus:pds-outline-2 focus:pds-outline-offset-4 focus:pds-outline-focus"
        {...props}
      >
        {children}
      </BaseLink>
    </li>
  );
};

const GeoMenuItem: React.FC<{ geoMenu: GeoMenu | undefined }> = ({ geoMenu }) => {
  const { t, i18n } = useTranslation();

  if (!geoMenu) {
    return null;
  }

  const triggerTitle =
    geoMenu?.countryLinks.find(item => {
      const currentLocale = `${i18n.language}-us`;
      return item.hreflang === currentLocale;
    })?.website_name ?? t('US (language)');

  return (
    <React.Fragment key={GEO_MENU_ID}>
      <NavigationMenuItem value={GEO_MENU_ID} className={separator()}>
        <NavigationMenuTrigger
          className="pds-items-center pds-border-none pds-bg-opacity-none !pds-text-body-md pds-font-medium pds-uppercase pds-tracking-[2.5px] pds-text-surface hover:pds-bg-transparent hover:pds-text-surface focus:pds-border-solid focus:pds-border-surface focus:pds-bg-transparent focus:pds-text-surface focus:pds-outline-offset-1 focus:pds-outline-focus focus-visible:pds-border disabled:pds-pointer-events-none disabled:pds-opacity-medium data-[active]:pds-bg-transparent data-[state=open]:pds-bg-transparent"
          aria-label={`${t('Select your country. Current selection is')} ${triggerTitle}`}
        >
          <div className="pds-flex pds-items-center pds-gap-3">
            {triggerTitle}
            <Icon path={mdiEarth} size={1} />
          </div>
        </NavigationMenuTrigger>

        <NavigationMenuContent
          {...forceMount}
          className="pds-left-unset pds-space-y-4 pds-p-4.5 pds-shadow-stronger"
        >
          <h2 className="pds-m-0 pds-text-title-md pds-font-extrabold">{t('Select a Country')}</h2>

          <ul className="pds-m-0 pds-overflow-clip pds-rounded pds-p-0 md:pds-w-[270px]">
            {geoMenu.countryLinks.map(item => (
              <ParagraphMenuListItem
                key={item.website_name}
                title={item.website_name}
                to={item.website_url}
                target={getAnchorTarget(item.website_url)}
                hrefLang={item.hreflang}
                lang={item.hreflang.split('-')[0]}
                hideExternalIcon
              >
                {item.website_name}
              </ParagraphMenuListItem>
            ))}
          </ul>
        </NavigationMenuContent>
      </NavigationMenuItem>
    </React.Fragment>
  );
};

const Menu: React.FC<{
  className: string;
  menuItems: IStorageSubMenu[];
  geoMenu?: GeoMenu;
  isFooter?: boolean;
  translations: Translations['edges'][number]['node'] | undefined;
}> = ({ className, menuItems, geoMenu, translations }) => {
  const { t } = useTranslation();
  const { enableGeoDropDownMenu } = useFlags<LDFlags>();

  const renderMenuItem = (menuItem: IStorageSubMenu) => {
    const hasSubMenu = menuItem.links.length > 0;

    const handleAnalytics = () => {
      sendIt({
        event: 'nav_click',
        eventParams: {
          nav_item: menuItem.link.title,
          nav_type: 'header',
        },
      });
    };

    if (hasSubMenu) {
      const isHeadingLink = menuItem.link.uri !== 'route:<nolink>';

      // Dropdown menu
      return (
        <React.Fragment key={menuItem.id}>
          <NavigationMenuItem className={separator()}>
            <NavigationMenuTrigger className="pds-items-center pds-border-none pds-bg-opacity-none !pds-text-body-md pds-font-medium pds-uppercase pds-tracking-[2.5px] pds-text-surface hover:pds-bg-transparent hover:pds-text-surface focus:pds-border-solid focus:pds-border-surface focus:pds-bg-transparent focus:pds-text-surface focus:pds-outline-offset-1 focus:pds-outline-focus focus-visible:pds-border disabled:pds-pointer-events-none disabled:pds-opacity-medium data-[active]:pds-bg-transparent data-[state=open]:pds-bg-transparent">
              {menuItem.link.title}
            </NavigationMenuTrigger>

            <NavigationMenuContent
              {...forceMount}
              className="pds-space-y-4 pds-p-4.5 pds-shadow-stronger data-[state=closed]:pds-hidden"
            >
              {isHeadingLink ? (
                <h2 className="pds-m-0">
                  <BaseLink
                    analytics={false}
                    to={menuItem.link.url}
                    className="pds-text-title-md pds-font-extrabold pds-text-surface-text pds-no-underline focus:pds-outline-2 focus:pds-outline-offset-4 focus:pds-outline-focus"
                    onClick={handleAnalytics}
                    {...menuItem.link.options?.attributes}
                  >
                    {menuItem.link.title}
                  </BaseLink>
                </h2>
              ) : (
                <h2 className="pds-m-0 pds-text-title-md pds-font-extrabold">
                  {menuItem.link.title}
                </h2>
              )}

              <ul className="pds-overflow-clip pds-rounded pds-p-0 md:pds-w-[270px]">
                {menuItem.links.map(item => (
                  <ParagraphMenuListItem
                    key={item.title}
                    title={item.title}
                    to={item.url}
                    {...item.options?.attributes}
                  >
                    {item.title}
                  </ParagraphMenuListItem>
                ))}
              </ul>
            </NavigationMenuContent>
          </NavigationMenuItem>
        </React.Fragment>
      );
    }

    if (!menuItem.link) {
      return null;
    }

    return (
      <React.Fragment key={menuItem.id}>
        <NavigationMenuItem className={separator()}>
          <NavigationMenuLink
            asChild
            className="pds-group/item pds-flex pds-bg-opacity-none pds-px-4 pds-py-3 pds-text-body-md pds-font-medium pds-tracking-[2.5px] pds-text-surface pds-no-underline pds-transition-colors"
          >
            <BaseLink
              analytics={false}
              to={menuItem.link?.url}
              onClick={handleAnalytics}
              {...menuItem.link.options?.attributes}
            >
              <span className="pds-relative pds-text-center pds-text-body-md pds-font-medium pds-uppercase [--tw-content:''] after:pds-animated-underline group-hover/item:after:pds-w-full group-hover/item:after:pds-bg-[currentColor]">
                {menuItem.link?.title}
              </span>
            </BaseLink>
          </NavigationMenuLink>
        </NavigationMenuItem>
      </React.Fragment>
    );
  };

  return (
    <NavigationMenu
      aria-label="Utility"
      className={`pds-left-[-1px] pds-z-[999] [&_>_div]:!pds-h-[40px] ${className}`}
    >
      <NavigationMenuList className="pds-h-[40px]">
        {menuItems.map(renderMenuItem)}

        {enableGeoDropDownMenu && geoMenu && <GeoMenuItem geoMenu={geoMenu} />}

        {!enableGeoDropDownMenu && translations && (
          <NavigationMenuItem>
            <NavigationMenuLink
              asChild
              className="pds-group/item pds-flex pds-bg-opacity-none pds-px-4 pds-py-3 pds-font-medium pds-tracking-[2.5px] pds-text-surface pds-no-underline pds-transition-colors"
            >
              <BaseLink
                analytics={false}
                to={translations.path.alias}
                lang={translations.langcode}
                onClick={() => {
                  sendIt({
                    event: 'nav_click',
                    eventParams: {
                      nav_item: translations.langcode,
                      nav_type: 'header',
                    },
                  });
                }}
              >
                <div className="pds-text-body-md pds-font-medium pds-uppercase [--tw-content:''] after:pds-animated-underline group-hover/item:after:pds-w-full group-hover/item:after:pds-bg-[currentColor]">
                  <div className="pds-flex pds-items-center pds-gap-3">
                    {t('US (language-inverse)')}
                    <Icon path={mdiEarth} size={1} />
                  </div>
                </div>
              </BaseLink>
            </NavigationMenuLink>
          </NavigationMenuItem>
        )}

        <NavigationMenuIndicator className="[&_>_div]:pds-border-gray-200" />
      </NavigationMenuList>

      <NavigationMenuViewport />
    </NavigationMenu>
  );
};

interface ParagraphProps extends Paragraph {
  link: LinkProps;
  menu: [LinkProps];
  relationships: { menus?: IStorageSubMenu[] };
}

interface ParagraphMenuProps {
  node: ParagraphProps;
  props?: {
    location?: string;
    translations?: Translations;
    geoMenu?: GeoMenu;
    isFooter?: boolean;
  };
}

const ParagraphMenu: React.FC<ParagraphMenuProps> = function ({ node, props = {} }) {
  const { enableGeoDropDownMenu } = useFlags<LDFlags>();

  const classes = node.behavior_settings?.decoupled_styles?.classes || [];
  const allClasses = [...classes, 'menu'];
  const { isFooter = false } = props;
  const translations = props?.translations?.edges[0]?.node;

  const globalData = useStaticQuery<{
    allStorageGlobalContent: { edges: { node: IStorageGlobalContent }[] };
  }>(graphql`
    {
      allStorageGlobalContent(filter: { field_type: { eq: "privacy_link_modal_content" } }) {
        edges {
          node {
            field_type
            field_unfiltered_html {
              value
            }
            relationships {
              image: field_media {
                ...MediaImage
                relationships {
                  field_media_image {
                    gatsbyImage(layout: CONSTRAINED, aspectRatio: 2, width: 76, placeholder: NONE)
                  }
                }
              }
            }
          }
        }
      }
    }
  `);

  if (node.relationships.menus?.length && node?.relationships?.menus?.length > 0) {
    return (
      <Menu
        className={classes.join(' ')}
        menuItems={node.relationships.menus}
        translations={translations}
        geoMenu={props.geoMenu}
      />
    );
  }

  if (!node?.menu.length) {
    return null;
  }

  const globalContent = globalData.allStorageGlobalContent.edges[0]?.node;
  const hasImage = !isEmpty(globalContent?.relationships.image);
  const imageIcon = globalContent?.relationships.image as MediaImage;

  return (
    <ul className={`pds-list-none ${allClasses.join(' ')}`}>
      {node.menu.map((link: LinkProps, index) => {
        const attributes = link?.options?.attributes || {};
        const { is_privacy_link, ...newAttributes } = attributes;

        const handleAnalytics = () => {
          sendIt({
            event: 'nav_click',
            eventParams: {
              nav_item: link.title,
              nav_type: isFooter ? 'footer' : 'header',
            },
          });
        };

        if (is_privacy_link) {
          return (
            <li key={index} className="d-flex pds-items-center">
              <BaseLink
                analytics={false}
                to="#toggleOneTrust"
                onClick={() => {
                  handleAnalytics();
                  window.OneTrust.ToggleInfoDisplay();
                }}
                className="pds-group pds-flex pds-py-2 pds-pl-[0.75rem] pds-leading-4 pds-tracking-[2.5px] pds-no-underline"
                {...newAttributes}
              >
                <span className="pds-relative pds-uppercase [--tw-content:''] after:pds-animated-underline group-hover:after:pds-w-full group-hover:after:pds-bg-[currentColor] lg:pds-text-center">
                  {link.title}
                </span>
              </BaseLink>

              {hasImage && (
                <MediaImage
                  {...(imageIcon as MediaImage)}
                  className="pds-mx-2 pds-mb-2 pds-w-[38px] pds-rounded-none"
                />
              )}
            </li>
          );
        }

        return (
          <li key={index}>
            <BaseLink
              analytics={false}
              to={link.url}
              onClick={handleAnalytics}
              className="pds-group pds-flex pds-px-[0.75rem] pds-py-2 pds-leading-4 pds-tracking-[2.5px] pds-no-underline"
              {...newAttributes}
            >
              <span className="pds-relative pds-uppercase [--tw-content:''] after:pds-animated-underline group-hover:after:pds-w-full group-hover:after:pds-bg-[currentColor] lg:pds-text-center">
                {link.title}
              </span>
            </BaseLink>
          </li>
        );
      })}

      {!enableGeoDropDownMenu && translations && (
        <li key="translation-link">
          <BaseLink
            analytics={false}
            to={translations.path.alias}
            onClick={() => {
              sendIt({
                event: 'nav_click',
                eventParams: {
                  nav_item: translations.langcode,
                  nav_type: 'header',
                },
              });
            }}
            className="pds-group pds-flex pds-px-[0.75rem] pds-py-2 pds-leading-4 pds-tracking-[2.5px] pds-no-underline"
            lang={translations.langcode}
          >
            <span className="pds-relative pds-text-center pds-uppercase [--tw-content:''] after:pds-animated-underline group-hover:after:pds-w-full group-hover:after:pds-bg-[currentColor]">
              {translations.langcode === 'es' ? 'Español' : 'English'}{' '}
              <Icon path={mdiEarth} size={0.7} />
            </span>
          </BaseLink>
        </li>
      )}
    </ul>
  );
};

export default ParagraphMenu;
